import { Button } from "@peer-start/ui";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import {
  emptyOnboardingAnswers,
  resolveOnboardingAnswers,
  saveOnboardingAnswers,
  type OnboardingAnswers,
} from "../components/onboarding/onboarding-answers.js";
import { careEnvironments } from "../components/onboarding/onboarding-options.js";
import {
  OnboardingAboutStep,
  OnboardingCredentialsStep,
  OnboardingExperienceStep,
} from "../components/onboarding/OnboardingSteps.js";
import { OnboardingStepper } from "../components/onboarding/OnboardingStepper.js";
import { clearProductTourProgress } from "../../passport/components/product-tour-storage.js";

const STEPS = ["about", "credentials", "experience"] as const;

export function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bootstrap, persona, runCommand, commandPending } = useAppContext();
  const employee = persona.kind === "employee" ? persona : null;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<OnboardingAnswers>(() =>
    employee
      ? resolveOnboardingAnswers({
          employeeId: employee.id,
          roleId: employee.roleId,
          workContext: employee.workContext,
          onboardingComplete: employee.onboardingComplete,
        })
      : emptyOnboardingAnswers(),
  );

  useEffect(() => {
    if (!employee) return;
    setAnswers(
      resolveOnboardingAnswers({
        employeeId: employee.id,
        roleId: employee.roleId,
        workContext: employee.workContext,
        onboardingComplete: employee.onboardingComplete,
      }),
    );
  }, [employee?.id, employee?.roleId, employee?.workContext, employee?.onboardingComplete]);

  const steps = useMemo(
    () => [
      { id: STEPS[0], label: t("onboarding.steps.about") },
      { id: STEPS[1], label: t("onboarding.steps.credentials") },
      { id: STEPS[2], label: t("onboarding.steps.experience") },
    ],
    [t],
  );

  if (!employee) return null;

  const careLabel =
    careEnvironments.find((entry) => entry.value === answers.careEnvironment)?.labelKey ?? null;
  const workContext = careLabel ? t(careLabel) : t("onboarding.skippedContext");
  const canContinueAbout = Boolean(answers.roleId && answers.careEnvironment && answers.years);
  const patch = (partial: Partial<OnboardingAnswers>) =>
    setAnswers((current) => ({ ...current, ...partial }));

  const finish = async (context: string) => {
    saveOnboardingAnswers(employee.id, answers);
    clearProductTourProgress(employee.id);
    await runCommand({
      type: "onboarding_completed",
      employeeId: employee.id,
      roleId: answers.roleId,
      workContext: context,
    });
    navigate("/me?overview=1&tour=1");
  };

  return (
    <div className="flex min-h-[calc(100dvh-10.5rem)] flex-col md:min-h-[calc(100dvh-8.5rem)]">
      <div className="mx-auto w-full max-w-3xl flex-1">
        <div className="mb-6 flex justify-end">
          <button
            className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="onboarding.skip"
            disabled={commandPending || !answers.roleId}
            onClick={() => void finish(t("onboarding.skippedContext"))}
            type="button"
          >
            {t("onboarding.skip")}
          </button>
        </div>

        <OnboardingStepper currentStep={step} label={t("onboarding.stepperLabel")} steps={steps} />

        <div className="min-h-0">
          {step === 0 ? (
            <OnboardingAboutStep answers={answers} onPatch={patch} roles={bootstrap.roles} />
          ) : null}
          {step === 1 ? <OnboardingCredentialsStep answers={answers} onPatch={patch} /> : null}
          {step === 2 ? <OnboardingExperienceStep answers={answers} onPatch={patch} /> : null}
        </div>
      </div>

      <div className="sticky bottom-0 mt-auto w-full border-t border-border bg-background pt-4 pb-2 md:pb-0">
        <div className="flex w-full items-center justify-between gap-3">
          <Button
            controlId="onboarding.previous"
            disabled={step === 0 || commandPending}
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            type="button"
            variant="secondary"
          >
            {t("onboarding.previous")}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              controlId="onboarding.next"
              disabled={(step === 0 && !canContinueAbout) || commandPending}
              onClick={() => setStep((current) => Math.min(STEPS.length - 1, current + 1))}
              type="button"
            >
              {t("onboarding.next")}
            </Button>
          ) : (
            <Button
              controlId="onboarding.complete"
              disabled={commandPending || !canContinueAbout}
              onClick={() => void finish(workContext)}
              type="button"
            >
              {t("onboarding.complete")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
